import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Badge } from "./ui/badge";
import { Pagination } from "./ui/pagination";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { FileText, Plus, Search, Edit, Trash2, Eye, Calendar, Building, DollarSign, Loader2, RefreshCw, Filter } from "lucide-react";
import { toast } from "sonner";
import { purchaseOrderApiService, PoHeader } from "../services/purchaseOrderApi";
import { PermissionGate } from "./ui/PermissionGate";
import { useAuth } from "../contexts/AuthContext";
import { useBranch } from "../contexts/BranchContext";

export function PurchaseOrderListingPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { selectedBranch } = useBranch();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>(searchParams.get('status') || 'All');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  
  // API state
  const [purchaseOrders, setPurchaseOrders] = useState<PoHeader[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  
  // Server-side pagination state
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [serverCurrentPage, setServerCurrentPage] = useState(1);

  // Refs to prevent duplicate API calls
  const isFetchingRef = useRef(false);
  const lastBranchIdRef = useRef<number | null>(null);
  const initialMountRef = useRef(true);

  // Purchase Order management route (from backend permissions)
  const PURCHASE_ORDER_ROUTE = '/purchase-orders';

  // Fetch purchase orders from API with server-side pagination
  const fetchPurchaseOrders = useCallback(async (page: number = currentPage, search: string = debouncedSearchTerm) => {
    // Prevent duplicate concurrent calls
    if (isFetchingRef.current) {
      return;
    }

    // Resolve active branch id: selected branch from header overrides default
    const activeBranchId = selectedBranch || user?.defaultBranchId;

    // Validate branch ID
    if (!activeBranchId) {
      setError('User branch information is required. Please contact administrator.');
      setPurchaseOrders([]);
      return;
    }

    // Don't fetch if we're already fetching for the same branchId, page, and search
    if (lastBranchIdRef.current === activeBranchId && !initialMountRef.current) {
      // Only skip if nothing else changed - let page and search changes through
      // This check will be handled by the useEffect dependencies
    }

    try {
      isFetchingRef.current = true;
      setLoading(true);
      setError(null);
      
      lastBranchIdRef.current = activeBranchId;
      
      let response;
      
      // Use search API if there's a search term, otherwise use regular API
      if (search.trim()) {
        response = await purchaseOrderApiService.searchPurchaseOrders({
          q: search.trim(),
          branchId: activeBranchId
        });
      } else {
        // Pass status filter to API if not 'All'
        response = await purchaseOrderApiService.getPurchaseOrders({
          branchId: activeBranchId,
          page: page,
          pageSize: pageSize,
          status: statusFilter !== 'All' ? statusFilter : undefined
        });
      }
      
      // Handle the nested pagination structure from the API
      const orders = response.data || [];
      const pagination = response.pagination || {};
      const totalCount = pagination.totalCount || orders.length;
      const totalPages = pagination.totalPages || Math.ceil(totalCount / pageSize);
      const currentPage = pagination.currentPage || page;
      
      setPurchaseOrders(orders);
      setTotalCount(totalCount);
      setTotalPages(totalPages);
      setServerCurrentPage(currentPage);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch purchase orders';
      setError(errorMessage);
      toast.error(errorMessage);
      setPurchaseOrders([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
      initialMountRef.current = false;
    }
  }, [user?.defaultBranchId, selectedBranch, pageSize, statusFilter]);

  // Refresh data
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchPurchaseOrders(currentPage, debouncedSearchTerm);
    setRefreshing(false);
  };

  // Debounce search term for better performance
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Single effect to fetch data on branch/page/search changes
  // Wait for user to be loaded before making any API calls
  useEffect(() => {
    // Don't fetch until user is loaded
    if (!user) {
      return;
    }

    const activeBranchId = selectedBranch || user?.defaultBranchId;
    
    // Don't fetch if we don't have a branch ID yet
    if (!activeBranchId) {
      return;
    }

    // Don't fetch if we're already fetching for the same branch
    if (isFetchingRef.current && lastBranchIdRef.current === activeBranchId) {
      return;
    }

    // Reset page to 1 when branch changes
    if (lastBranchIdRef.current !== null && lastBranchIdRef.current !== activeBranchId) {
      setCurrentPage(1);
    }

    fetchPurchaseOrders(1, debouncedSearchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch, user?.defaultBranchId, debouncedSearchTerm, user, statusFilter]);

  // Handle page changes separately (when branch/search hasn't changed)
  useEffect(() => {
    if (!user) return;
    
    const activeBranchId = selectedBranch || user?.defaultBranchId;
    if (!activeBranchId) return;
    
    // Skip if this is the initial mount (handled by the effect above)
    if (initialMountRef.current) return;
    
    // Only fetch if page changed but branch/search didn't change
    if (lastBranchIdRef.current === activeBranchId && !isFetchingRef.current) {
      fetchPurchaseOrders(currentPage, debouncedSearchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Available status options
  const statusOptions = ['All', 'DRAFT', 'APPROVED', 'CLOSED'];

  // Update URL when status filter changes and reset to page 1
  useEffect(() => {
    if (statusFilter && statusFilter !== 'All') {
      setSearchParams({ status: statusFilter });
    } else {
      setSearchParams({});
    }
    // Reset to page 1 when status filter changes
    setCurrentPage(1);
  }, [statusFilter, setSearchParams]);

  // With server-side filtering, we use the orders directly from API
  const paginatedOrders = purchaseOrders;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleNewOrder = () => {
    navigate('/purchase-orders/new');
  };

  const handleEditOrder = (order: PoHeader) => {
    navigate(`/purchase-orders/${order.id}/edit`);
  };

  const handleViewOrder = (order: PoHeader) => {
    navigate(`/purchase-orders/${order.id}/view`);
  };

  const handlePdfView = (order: PoHeader) => {
    const url = `/purchase-orders/${order.id}/pdf`;
    window.open(url, '_blank');
  };

  const handleDeleteOrder = async (order: PoHeader) => {
    if (!order.id) {
      toast.error('Invalid order ID');
      return;
    }

    try {
      await purchaseOrderApiService.deletePurchaseOrder(order.id.toString());
      toast.success(`Purchase order ${order.poNumber} deleted successfully!`);
      // Refresh the list after deletion
      await fetchPurchaseOrders();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete purchase order';
      toast.error(errorMessage);
    }
  };

  const getStatusColor = useCallback((status: string) => {
    switch (status?.toLowerCase()) {
      case 'draft': return 'bg-gray-100 text-gray-700';
      case 'pending approval': return 'bg-yellow-100 text-yellow-700';
      case 'approved': return 'bg-blue-100 text-blue-700';
      case 'ordered': return 'bg-purple-100 text-purple-700';
      case 'received': return 'bg-green-100 text-green-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      case 'closed': return 'bg-orange-100 text-orange-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  }, []);

  const getPriorityColor = useCallback((priority: string) => {
    switch (priority?.toLowerCase()) {
      case 'low': return 'bg-green-100 text-green-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'high': return 'bg-orange-100 text-orange-700';
      case 'urgent': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  }, []);

  // Format date helper - memoized for performance
  const formatDate = useCallback((dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Invalid Date';
    }
  }, []);

  // Format currency helper - memoized for performance
  const formatCurrency = useCallback((amount: number) => {
    if (amount === null || amount === undefined) return '₹0';
    return `₹${amount.toLocaleString()}`;
  }, []);



  // Show error if user branch information is missing
  if (!user?.defaultBranchId && !loading) {
    return (
      <div className="space-y-6">
        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardContent className="p-6 text-center">
            <div className="text-yellow-600 mb-4">
              <FileText className="h-12 w-12 mx-auto mb-3" />
              <p className="text-lg font-semibold">Branch Information Required</p>
              <p className="text-sm text-gray-600 mt-2">
                Your account needs to be associated with a branch to view purchase orders. 
                Please contact your administrator to set up your branch information.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error && !loading) {
    return (
      <div className="space-y-6">
        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardContent className="p-6 text-center">
            <div className="text-red-600 mb-4">
              <FileText className="h-12 w-12 mx-auto mb-3" />
              <p className="text-lg font-semibold">Error Loading Purchase Orders</p>
              <p className="text-sm text-gray-600 mt-2">{error}</p>
            </div>
            <Button onClick={handleRefresh} className="bg-primary-600 hover:bg-primary-700">
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
        <CardHeader className="bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-white/20 flex items-center justify-center">
                <FileText className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-xl">Purchase Order Details</CardTitle>
                <p className="text-primary-100 text-sm">Manage your purchase orders and procurement</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleRefresh}
                variant="outline"
                size="sm"
                disabled={refreshing}
                className="bg-white/20 text-white border-white/30 hover:bg-white/30"
              >
                {refreshing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
              <PermissionGate route={PURCHASE_ORDER_ROUTE} action="create">
                <Button
                  onClick={handleNewOrder}
                  className="bg-white text-primary-600 hover:bg-primary-50 shadow-lg"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Purchase Order
                </Button>
              </PermissionGate>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {/* Search and Filters */}
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search purchase orders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-primary-200 focus:border-primary-400 focus:ring-primary-400"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px] border-primary-200 focus:border-primary-400 focus:ring-primary-400">
                  <SelectValue placeholder="Filter by status">
                    {statusFilter === 'All' ? statusFilter : statusFilter.toUpperCase()}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((status) => (
                    <SelectItem key={status} value={status}>
                      {status === 'All' ? status : status.toUpperCase()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="text-sm text-gray-600">
              {statusFilter === 'All' 
                ? `${paginatedOrders.length} of ${totalCount} orders`
                : `${paginatedOrders.length} of ${totalCount} order${totalCount !== 1 ? 's' : ''} (${statusFilter.toUpperCase()})`
              }
            </div>
          </div>

          {/* Purchase Orders Table */}
          <div className="border border-primary-200 rounded-lg overflow-hidden bg-white">
            {loading ? (
              <div className="p-8 text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-primary-600" />
                <p className="text-secondary-500">Loading purchase orders...</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                    <TableHead>PO Number</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Order Date</TableHead>
                    <TableHead>Expected Delivery</TableHead>
                    <TableHead>Total Amount</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedOrders.map((order) => (
                    <TableRow key={order.id} className="hover:bg-primary-50/50">
                      <TableCell className="font-medium">{order.poNumber || 'N/A'}</TableCell>
                                          <TableCell>
                      <div className="flex items-center gap-2">
                        <Building className="h-3 w-3 text-gray-400" />
                        <span>{order.supplier?.supplierName || 'N/A'}</span>
                      </div>
                    </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="h-3 w-3 text-gray-400" />
                          <span>{formatDate(order.purchaseDate)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="h-3 w-3 text-gray-400" />
                          <span>{formatDate(order.expectedDeliveryDate)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{formatCurrency(order.totalAmount)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getPriorityColor(order.priority)} border-0 text-xs`}>
                          {order.priority || 'N/A'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getStatusColor(order.status)} border-0`}>
                          {order.status?.toUpperCase() || 'N/A'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePdfView(order)}
                            className="border-accent-200 hover:bg-accent-50 hover:border-accent-300 text-accent-600"
                            title="View PDF (New Tab)"
                          >
                            <FileText className="h-3 w-3" />
                          </Button>
                          <PermissionGate route={PURCHASE_ORDER_ROUTE} action="edit">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditOrder(order)}
                              className="border-primary-200 hover:bg-primary-50 hover:border-primary-300 text-primary-600"
                              title="Edit Order"
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate route={PURCHASE_ORDER_ROUTE} action="delete">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteOrder(order)}
                              className="border-red-200 hover:bg-red-50 hover:border-red-300 text-red-600"
                              title="Delete Order"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </PermissionGate>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          {!loading && paginatedOrders.length === 0 && (
            <div className="text-center py-8 text-secondary-500">
              <FileText className="h-12 w-12 mx-auto mb-3 text-primary-300" />
              <p>No purchase orders found matching your search criteria.</p>
            </div>
          )}

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="mt-6">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      {/* <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Orders</p>
                <p className="text-2xl font-semibold text-redpink-700">{totalCount}</p>
              </div>
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-redpink-500 to-redpink-600 flex items-center justify-center">
                <FileText className="h-5 w-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pending Approval</p>
                <p className="text-2xl font-semibold text-yellow-700">
                  {(purchaseOrders || []).filter(o => o.status?.toLowerCase() === 'pending approval').length}
                </p>
              </div>
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-yellow-500 to-yellow-600 flex items-center justify-center">
                <FileText className="h-5 w-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Received</p>
                <p className="text-2xl font-semibold text-green-700">
                  {(purchaseOrders || []).filter(o => o.status?.toLowerCase() === 'received').length}
                </p>
              </div>
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
                <FileText className="h-5 w-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Value</p>
                <p className="text-2xl font-semibold text-blue-700">
                  {formatCurrency((purchaseOrders || []).reduce((sum, o) => sum + (o.totalAmount || 0), 0))}
                </p>
              </div>
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div> */}


    </div>
  );
}